import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { LockOutlined } from '@material-ui/icons';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import SearchFeaturePageShell from '../../components/shell/SearchFeaturePageShell';
import { useLocalizedMessage } from '../../util/useLocalizedMessage';
import { useShellAppContext } from '../shell/context/useShellAppContext';
import { useCloudDeploymentStatus } from './useCloudDeploymentStatus';

function CloudManagedInterfaceGate({ pageId, shopId, children }) {
  const deployment = useCloudDeploymentStatus({
    enabled: true,
    shopId,
  });
  const localizeMessage = useLocalizedMessage();

  const managedExternally = deployment.status?.managedExternally === true;
  const ownershipUnverified = Boolean(
    deployment.status && deployment.status.canEditInterface !== true && !managedExternally,
  );

  if ((deployment.error && !deployment.status) || ownershipUnverified) {
    return (
      <SearchFeaturePageShell pageId={pageId}>
        <Alert
          severity="error"
          action={(
            <Button color="inherit" size="small" onClick={() => deployment.refresh()}>
              {localizeMessage('wizard.searchSetup.configuration.retry')}
            </Button>
          )}
        >
          {localizeMessage('cloud.release.error')}
        </Alert>
      </SearchFeaturePageShell>
    );
  }

  if (managedExternally) {
    return (
      <SearchFeaturePageShell pageId={pageId}>
        <Paper variant="outlined" style={{ padding: 28, borderRadius: 10 }}>
          <Box display="flex" alignItems="flex-start" gridGap={16}>
            <Box
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              flex="0 0 42px"
              width={42}
              height={42}
              borderRadius={10}
              bgcolor="#e4efff"
              color="#285a9e"
            >
              <LockOutlined />
            </Box>
            <Box>
              <Typography variant="h6">
                {localizeMessage('cloud.release.managed.title')}
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 6 }}>
                {localizeMessage('cloud.release.managed.description')}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </SearchFeaturePageShell>
    );
  }

  if (deployment.isLoading && !deployment.status) {
    return (
      <Box display="flex" minHeight={320} alignItems="center" justifyContent="center">
        <CircularProgress size={40} />
      </Box>
    );
  }

  return <>{children}</>;
}

CloudManagedInterfaceGate.propTypes = {
  pageId: PropTypes.string.isRequired,
  shopId: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

export default function CloudInterfaceAccessGate({ pageId, shopId, children }) {
  const { productProfile } = useShellAppContext();
  const platform = useSelector(state => state.settings?.platform);
  const settingsLoaded = useSelector(state => state.settings?.isFetched === true);
  if (productProfile?.id !== 'cloud') return <>{children}</>;
  if (!settingsLoaded) {
    return (
      <Box display="flex" minHeight={320} alignItems="center" justifyContent="center">
        <CircularProgress size={40} />
      </Box>
    );
  }
  if (platform === 'manual') return <Redirect to={`/app/${shopId}/home`} />;
  return (
    <CloudManagedInterfaceGate pageId={pageId} shopId={shopId}>
      {children}
    </CloudManagedInterfaceGate>
  );
}

CloudInterfaceAccessGate.propTypes = {
  pageId: PropTypes.string.isRequired,
  shopId: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};
